import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { User } from '../auth/auth.types';
import {
  AcceptAgencyInvitationPayload,
  Agency,
  AgencyInvitationTokenInfo,
  AgencyMemberRole,
  InviteAgencyMemberPayload,
} from './agency.types';

@Injectable({ providedIn: 'root' })
export class AgencyService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/agencies`;

  /** The caller's agency and its active team. */
  getMine(): Observable<Agency> {
    return this.http.get<Agency>(`${this.base}/mine/`);
  }

  /** Invite an agent to the caller's agency (ADMIN only). Returns the updated agency. */
  inviteMember(payload: InviteAgencyMemberPayload): Observable<Agency> {
    return this.http.post<Agency>(`${this.base}/mine/members/`, payload);
  }

  /** Change a member's role (ADMIN only). */
  setMemberRole(memberShortId: string, role: AgencyMemberRole): Observable<Agency> {
    return this.http.patch<Agency>(`${this.base}/mine/members/${memberShortId}/`, { role });
  }

  /** Deactivate a member (ADMIN only). */
  removeMember(memberShortId: string): Observable<Agency> {
    return this.http.delete<Agency>(`${this.base}/mine/members/${memberShortId}/`);
  }

  /** Cancel a pending invitation (ADMIN only). */
  cancelInvitation(inviteShortId: string): Observable<Agency> {
    return this.http.post<Agency>(
      `${this.base}/mine/invitations/${inviteShortId}/cancel/`,
      {},
    );
  }

  /** Public pre-accept summary for the emailed invitation link. */
  invitationInfo(token: string): Observable<AgencyInvitationTokenInfo> {
    return this.http.get<AgencyInvitationTokenInfo>(`${this.base}/invitations/info/`, {
      params: { token },
    });
  }

  /** Accept an invitation — creates the membership and signs the user in. */
  acceptInvitation(payload: AcceptAgencyInvitationPayload): Observable<User> {
    return this.http.post<User>(`${this.base}/invitations/accept/`, payload);
  }
}
